import { useRef, useState, type RefObject } from 'react';
import { useReturnFocus } from './Dialog';
import * as Dialog from '@radix-ui/react-dialog';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatOffset, speakerName, transcriptToText, type Transcript } from '@avslack/shared';
import { getTranscript, sendMessage, shareTranscript } from '../lib/api';
import { useUsers } from '../data/users';
import { Avatar } from './Avatar';
import { Icon } from './Icon';
import { ProjectflowDialog } from './ProjectflowDialog';
import { startProjectflowLink, useProjectflowStatus } from '../data/projectflow';
import { useDismiss } from '../lib/dismiss';

/**
 * Az oleles atirata.
 *
 * **Csak az latja, aki ott volt** - vagy akivel megosztottak. A szerver 404-et
 * ad mindenki masnak, tehat ez a panel nem tud tobbet mutatni, mint amennyihez
 * a nezonek joga van; a jogosultsag nem itt dol el.
 *
 * Az atirat CELJA a tovabbhasznalat: masolhato, es egy kattintassal bekerul a
 * csatornaba, ahonnan barhova tovabb megy (feladatkezelo, level, jegyzet).
 * Egy atirat, amit csak nezni lehet, fel atirat.
 */
export function TranscriptDialog({
  huddleId,
  open,
  onOpenChange,
}: {
  huddleId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { nameOf } = useUsers();
  const queryClient = useQueryClient();
  const [showRaw, setShowRaw] = useState(false);
  /*
    A megosztas-panel allapota ITT el, nem a laban: az Escape-et a Radix
    parbeszed a sajat (capture) figyelojevel elkapja, es az EGESZ ablakot
    zarna. Ha a panel nyitva van, az Escape csak a panelt zarja.
  */
  const [shareOpen, setShareOpen] = useState(false);
  const shareTriggerRef = useRef<HTMLButtonElement>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['transcript', huddleId],
    queryFn: () => getTranscript(huddleId),
    enabled: open,
    /*
      Amig keszul, ranezunk ujra.

      A feldolgozas a hivas utan meg par masodpercig tart; enelkul a panel
      "készül" allapotban ragadna, amig a felhasznalo ujra meg nem nyitja.
    */
    refetchInterval: (query) =>
      query.state.data?.state === 'processing' || query.state.data?.state === 'recording'
        ? 4000
        : false,
  });

  // Bezaraskor a fokusz vissza a nyito elemre (lasd Dialog.tsx).
  const returnFocus = useReturnFocus(open);
  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) setShareOpen(false);
        onOpenChange(next);
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content
          onCloseAutoFocus={returnFocus}
          aria-describedby={undefined}
          onEscapeKeyDown={(event) => {
            if (!shareOpen) return;
            event.preventDefault();
            setShareOpen(false);
            shareTriggerRef.current?.focus();
          }}
          className="fixed left-1/2 top-1/2 z-50 flex max-h-[calc(var(--app-height,100dvh)*0.85)] w-[min(44rem,92vw)] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-card border border-border bg-surface-raised shadow-modal"
        >
          <header className="flex items-center gap-2.5 border-b border-border-soft px-4 py-3">
            <Icon name="atirat" />
            <Dialog.Title className="min-w-0 flex-1 truncate font-display text-sm font-semibold">
              {data?.title ?? 'Ölelés'} — átirat
            </Dialog.Title>
            <Dialog.Close
              aria-label="Bezárás"
              className="rounded-control px-1.5 py-1 text-ink-faint transition-colors hover:bg-surface-hover hover:text-ink"
            >
              <Icon name="bezaras" size="sm" />
            </Dialog.Close>
          </header>

          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-3">
            {isLoading && <p className="text-sm text-ink-muted">Betöltés…</p>}

            {isError && (
              <p className="text-sm text-ink-muted">
                Ehhez az átirathoz nincs hozzáférésed, vagy nem készült.
              </p>
            )}

            {data && (
              <Body transcript={data} nameOf={nameOf} showRaw={showRaw} onShowRaw={setShowRaw} />
            )}
          </div>

          {data && data.segments.length > 0 && (
            <Footer
              transcript={data}
              nameOf={nameOf}
              shareOpen={shareOpen}
              onShareOpen={setShareOpen}
              shareTriggerRef={shareTriggerRef}
              onShared={(fresh) => queryClient.setQueryData(['transcript', huddleId], fresh)}
            />
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function Body({
  transcript,
  nameOf,
  showRaw,
  onShowRaw,
}: {
  transcript: Transcript;
  nameOf: (id: string, fallback?: string) => string;
  showRaw: boolean;
  onShowRaw: (value: boolean) => void;
}) {
  /*
    AZ ALLAPOTOT KIMONDJUK.

    "Nincs szoveg" onmagaban semmit nem mond: a felhasznalo nem tudna
    megkulonboztetni a meg keszulot a bukottol, es egy ures oldalt nezne,
    talalgatva.
  */
  if (transcript.state === 'off') {
    return <p className="text-sm text-ink-muted">Erről a hívásról nem készült átirat.</p>;
  }

  if (transcript.state === 'recording') {
    return (
      <p className="text-sm text-ink-muted">
        A hívás még tart. Az átirat a hívás végén, néhány másodpercen belül elkészül.
      </p>
    );
  }

  if (transcript.state === 'failed') {
    return (
      <p className="text-sm text-ink-muted">
        Az átirat nem készült el — a hang feldolgozása nem sikerült. Szólj az üzemeltetésnek.
      </p>
    );
  }

  const polishedCount = transcript.segments.filter((segment) => segment.polishedText).length;

  return (
    <div className="space-y-3">
      {transcript.state === 'processing' && (
        <p className="rounded-control bg-surface-sunken px-3 py-2 text-2xs text-ink-muted">
          {transcript.pendingChunks > 0
            ? `Készül — még ${transcript.pendingChunks} részlet van hátra. Az eddigi szöveg már látszik.`
            : 'A modell épp átnézi a szöveget. Az eddigi, nyers változat már látszik.'}
        </p>
      )}

      {/*
        A HIANY IS INFORMACIO.

        Egy hianyos atirat, ami teljesnek latszik, rosszabb, mint egy hianyos
        atirat, amirol tudjuk, hogy hianyos: az elsore rossz dontest lehet
        alapozni.
      */}
      {transcript.failedChunks > 0 && (
        <p className="rounded-control bg-[var(--danger-soft)] px-3 py-2 text-2xs text-ink">
          {transcript.failedChunks} részlet feldolgozása nem sikerült — az átirat hiányos.
        </p>
      )}

      {/*
        AKITOL NEM ERKEZETT HANG.

        Ez az a hiba, ami magatol nem latszik: az atirat keszen all, csak eppen
        valakinek a mondatai hianyoznak belole. Ha nem mondanank meg, a
        felhasznalo a szoveget hinne teljesnek - es arra alapozna.
      */}
      {transcript.missingAudio.length > 0 && (
        <p className="rounded-control bg-[var(--danger-soft)] px-3 py-2 text-2xs text-ink">
          {transcript.missingAudio.map((user) => nameOf(user.id, user.displayName)).join(', ')} —
          tőlük nem érkezett hang. Vagy végig némítva voltak, vagy náluk nem indult el a felvétel;
          az ő mondataik hiányoznak az átiratból.
        </p>
      )}

      {/*
        AZ ATNEZES lathato, es VISSZAKAPCSOLHATO.

        A nyers felismerest sosem irjuk felul: az a tany, amit a gep hallott.
        Az atnezett valtozat javit rajta - de ha a modell felreert valamit, az
        eredetihez kell tudni visszanyulni. Egy atirat, aminek nincs
        ellenorizheto eredetije, nem bizonyitek semmire.
      */}
      {polishedCount > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-control bg-surface-sunken px-3 py-2 text-2xs text-ink-muted">
          <span>
            {showRaw
              ? 'A felismerő nyers szövegét látod.'
              : `A modell ${polishedCount} sort javított.`}
          </span>
          <button
            type="button"
            onClick={() => onShowRaw(!showRaw)}
            className="ml-auto rounded-control bg-surface-raised px-2 py-0.5 font-medium text-ink transition-colors hover:bg-surface-hover"
          >
            {showRaw ? 'Javított szöveg' : 'Eredeti szöveg'}
          </button>
        </div>
      )}

      {transcript.polishError && (
        <p className="rounded-control bg-[var(--danger-soft)] px-3 py-2 text-2xs text-ink">
          Az átnézés nem sikerült: {transcript.polishError}
        </p>
      )}

      {transcript.segments.length === 0 ? (
        <p className="text-sm text-ink-muted">
          Nincs szöveg. Vagy nem hangzott el beszéd, vagy senkinél nem indult el a felvétel.
        </p>
      ) : (
        <ol className="space-y-2">
          {transcript.segments.map((segment) => (
            <li key={segment.id} className="flex gap-2.5">
              <span className="w-12 shrink-0 pt-0.5 text-right font-mono text-2xs text-ink-faint">
                {formatOffset(segment.startMs)}
              </span>
              <span className="shrink-0">
                {segment.speakerLabel ? (
                  // A telefonos hivonak nincs avatarja: a telefon a jele.
                  <span
                    aria-hidden="true"
                    className="grid size-6 place-items-center rounded-full bg-surface-hover text-ink-muted"
                  >
                    <Icon name="telefon" size="sm" />
                  </span>
                ) : (
                  <Avatar
                    userId={segment.userId ?? '?'}
                    name={nameOf(segment.userId ?? '')}
                    size="sm"
                  />
                )}
              </span>
              <p className="min-w-0 break-words text-sm text-ink">
                <span className="mr-1.5 font-medium">
                  {speakerName(segment, (id) => nameOf(id ?? '', 'Valaki'))}
                </span>
                {showRaw ? segment.text : (segment.polishedText ?? segment.text)}
              </p>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

/**
 * Amit az atirattal kezdeni lehet.
 *
 * Masolas es "be a csatornaba" - a ketto lefedi a tovabbhasznalat minden
 * utjat: a vagolaprol barhova (feladatkezelo, level), a csatornabol pedig
 * mindenki lathatja, aki nem volt bent.
 */
function Footer({
  transcript,
  nameOf,
  shareOpen,
  onShareOpen,
  shareTriggerRef,
  onShared,
}: {
  transcript: Transcript;
  nameOf: (id: string, fallback?: string) => string;
  shareOpen: boolean;
  onShareOpen: (open: boolean) => void;
  shareTriggerRef: RefObject<HTMLButtonElement | null>;
  onShared: (fresh: Transcript) => void;
}) {
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const [posted, setPosted] = useState(false);

  /*
    A tovabbkuldott szoveg a JAVITOTT valtozat.

    Ami a csatornaba vagy a vagolapra kerul, azt masok fogjak olvasni - oda a
    jobbik szoveg valo. Az eredeti nem vesz el: az atiratban egy kattintassal
    elohivhato.
  */
  const text = transcriptToText(
    {
      ...transcript,
      segments: transcript.segments.map((segment) => ({
        ...segment,
        text: segment.polishedText ?? segment.text,
      })),
    },
    (userId) => nameOf(userId ?? '', 'Valaki'),
  );

  const post = useMutation({
    mutationFn: () =>
      sendMessage(transcript.channelId, {
        text: `**Átirat — ${transcript.title ?? 'ölelés'}**\n\n${text}`,
        clientMsgId: `transcript-${transcript.huddleId}`,
      }),
    onSuccess: () => setPosted(true),
  });

  const [pfOpen, setPfOpen] = useState(false);
  const { data: projectflow } = useProjectflowStatus();

  const share = useMutation({
    mutationFn: (userIds: string[]) => shareTranscript(transcript.huddleId, userIds),
    onSuccess: onShared,
  });

  return (
    <footer className="relative flex flex-wrap items-center gap-2 border-t border-border-soft bg-surface-sunken px-4 py-2.5">
      <button
        type="button"
        onClick={() => {
          setCopyFailed(false);
          navigator.clipboard.writeText(text).then(
            () => setCopied(true),
            () => setCopyFailed(true),
          );
        }}
        className="rounded-control bg-surface-raised px-2.5 py-1 text-xs font-medium transition-colors hover:bg-surface-hover"
      >
        <Icon name="masolas" size="sm" /> {copied ? 'Kimásolva' : 'Másolás'}
      </button>
      <span role="status" className="sr-only">
        {copied ? 'Kimásolva a vágólapra.' : ''}
      </span>

      <button
        type="button"
        disabled={post.isPending || posted}
        onClick={() => post.mutate()}
        className="rounded-control bg-surface-raised px-2.5 py-1 text-xs font-medium transition-colors hover:bg-surface-hover disabled:opacity-50"
      >
        <Icon name="kuldes" size="sm" /> {posted ? 'Bekerült a csatornába' : 'Be a csatornába'}
      </button>

      {/*
        MEGOSZTAS EGY-EGY EMBERREL.

        Alapbol csak a resztvevok latjak az atiratot. Aki nem volt ott, kulon
        dontesbol kaphat hozzaferest - es ez a dontes nevre szol, nem egy
        globalis kapcsolora. A megosztas a keresesre is ervenyes: onnantol az
        atirat neki is elokerul.
      */}
      <button
        ref={shareTriggerRef}
        type="button"
        onClick={() => onShareOpen(!shareOpen)}
        aria-expanded={shareOpen}
        className="rounded-control bg-surface-raised px-2.5 py-1 text-xs font-medium transition-colors hover:bg-surface-hover"
      >
        <Icon name="meghivas" size="sm" /> Megosztás
      </button>

      {/*
        FELADAT AZ ATIRATBOL.

        Az atirat CELJA a tovabbhasznalat - itt lesz belole teendo. A javaslatot
        a felulet mutatja meg jovahagyasra: egy orai beszelgetesbol magatol
        letrejovo ot feladat, amit senki nem nezett at, tobbet art, mint hasznal.
      */}
      {projectflow?.enabled && (
        <button
          type="button"
          onClick={() => setPfOpen(true)}
          className="rounded-control bg-surface-raised px-2.5 py-1 text-xs font-medium transition-colors hover:bg-surface-hover"
        >
          <Icon name="pipa" size="sm" /> ProjectFlow
        </button>
      )}

      {pfOpen && (
        <ProjectflowDialog
          source={{ huddleId: transcript.huddleId }}
          linked={Boolean(projectflow?.linked)}
          onClose={() => setPfOpen(false)}
          onLink={startProjectflowLink}
        />
      )}

      {shareOpen && (
        <SharePanel
          transcript={transcript}
          nameOf={nameOf}
          pending={share.isPending}
          onPick={(userId) => share.mutate([userId])}
          onClose={() => onShareOpen(false)}
          triggerRef={shareTriggerRef}
        />
      )}

      {post.isError && (
        <span role="alert" className="text-2xs text-[var(--danger)]">
          A beküldés nem sikerült.
        </span>
      )}
      {share.isError && (
        <span role="alert" className="text-2xs text-[var(--danger)]">
          A megosztás nem sikerült.
        </span>
      )}
      {copyFailed && (
        <span role="alert" className="text-2xs text-[var(--danger)]">
          A másolás nem sikerült – jelöld ki és másold kézzel.
        </span>
      )}

      <span className="ml-auto text-2xs text-ink-faint">
        {transcript.participants.length} résztvevő
        {transcript.sharedWith.length > 0 && ` · megosztva ${transcript.sharedWith.length} fővel`}
        {share.isPending && ' · megosztás…'}
      </span>
    </footer>
  );
}

/**
 * Kivel osszuk meg.
 *
 * Aki mar latja - resztvevo vagy korabbi megosztott -, az ki sem kerul a
 * listaba: a mar meglevo hozzaferest nem lehet "megegyszer" megadni, es a
 * lista igy azt mutatja, ami valoban dontes.
 */
function SharePanel({
  transcript,
  nameOf,
  pending,
  onPick,
  onClose,
  triggerRef,
}: {
  transcript: Transcript;
  nameOf: (id: string, fallback?: string) => string;
  pending: boolean;
  onPick: (userId: string) => void;
  onClose: () => void;
  triggerRef: RefObject<HTMLButtonElement | null>;
}) {
  const { users } = useUsers();
  const panelRef = useRef<HTMLDivElement>(null);
  useDismiss(panelRef, onClose, { ignore: [triggerRef], returnFocus: triggerRef });
  const [query, setQuery] = useState('');

  const already = new Set([
    ...transcript.participants.map((user) => user.id),
    ...transcript.sharedWith.map((user) => user.id),
  ]);

  const candidates = users
    .filter((user) => !user.isBot && !already.has(user.id))
    .filter((user) => nameOf(user.id).toLowerCase().includes(query.trim().toLowerCase()))
    .slice(0, 8);

  return (
    <div
      ref={panelRef}
      className="absolute bottom-full left-4 z-20 mb-2 w-72 rounded-card border border-border bg-surface-raised p-3 shadow-modal"
    >
      <input
        aria-label="Kivel osszuk meg?"
        autoFocus
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Kivel osszuk meg?"
        className="w-full rounded-control border border-border bg-surface px-3 py-2 text-sm"
      />

      {candidates.length === 0 ? (
        <p className="mt-2 text-xs text-ink-faint">Nincs kivel megosztani.</p>
      ) : (
        <ul className="mt-2 space-y-0.5">
          {candidates.map((user) => (
            <li key={user.id}>
              <button
                type="button"
                disabled={pending}
                onClick={() => {
                  onPick(user.id);
                  onClose();
                }}
                className="flex w-full items-center gap-2 rounded-control px-2 py-1.5 text-left text-sm transition-colors hover:bg-surface-hover disabled:opacity-50"
              >
                <Avatar
                  userId={user.id}
                  name={user.displayName}
                  avatarUrl={user.avatarUrl}
                  size="sm"
                />
                <span className="truncate">{nameOf(user.id)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
